import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ValidationErrorInterface } from '@zaoblako/shared/common/interfaces/validation-error.interface';
import { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import {
  ExternalUserFormDto,
  ExternalUserFormInterface
} from '@zaoblako/shared/modules/security/dtos/external-user/external-user-form.dto';
import { ProfileFindInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-find.dto';
import { ProfileFormDto, ProfileFormInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-form.dto';
import { ProfileStatusEnum } from '@zaoblako/shared/modules/security/enums/profile-status.enum';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ExternalUserService } from '@zaoblako/web/core/services/security/external-user.service';
import { ProfileService } from '@zaoblako/web/core/services/security/profile.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ContragentMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contragent-multi-select/contragent-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { ExternalUserPermissionsWidgetComponent } from '@zaoblako/web/modules/security/dumbs/external-user-permissions-widget/external-user-permissions-widget.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputMaskModule } from 'primeng/inputmask';
import { MessagesModule } from 'primeng/messages';
import { forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'crm-external-user-edit-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    ButtonComponent,
    InputTextComponent,
    JoinPipe,
    CardModule,
    CheckboxModule,
    FormsModule,
    ContragentMultiSelectComponent,
    ClientAutoCompleteComponent,
    InputMaskModule,
    MessagesModule,
    ExternalUserPermissionsWidgetComponent
  ],
  templateUrl: './external-user-edit-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalUserEditFormComponent extends BaseFormComponent<ExternalUserFormInterface> implements OnInit {
  @Input() public readonly index: number;

  public user: ExternalUserFormInterface = <ExternalUserFormInterface>{};
  public profile: ProfileFormInterface = <ProfileFormInterface>{};
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public loadingProfile = false;

  public dto = ExternalUserFormDto;

  public profileError: ValidationErrorInterface<ProfileFormInterface>;

  public constructor(
    private readonly externalUserService: ExternalUserService,
    private readonly profileService: ProfileService,
    private readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.externalUserService
      .findById<ExternalUserFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (user) => {
          this.user = user as any;
          this.profile = user.profile;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveUser() {
    this.profileError = null;

    if (this.profile.phone === '') {
      this.profile.phone = null;
    }

    const [, errors] = this.validationService.parseAndValidate(this.profile, ProfileFormDto);

    if (errors) {
      this.profileError = errors.validationErrors;
      return;
    }

    if (this.checkValidate(this.user).valid) {
      this.formStoreService.setLoading(true, this.index);

      forkJoin([
        this.profileService.update<ProfileFormInterface, ProfileFindInterface>(this.profile),
        this.externalUserService.update<ExternalUserFormInterface, ExternalUserFindInterface>(this.user)
      ]).subscribe({
        next: ([profile, user]) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage(`Внешний пользователь "${profile?.name}" обновлен`);
          this.formStoreService.saveNext(user, this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }

  public changeEmail(email: string) {
    if (!email?.length) {
      return;
    }

    this.loadingProfile = true;
    this.cdRef.markForCheck();

    this.profileService.testEmail({ email }).subscribe({
      next: (data) => {
        if (data.profile) {
          this.profile = {
            email: data.profile.email,
            firstName: data.profile.firstName,
            lastName: data.profile.lastName,
            middleName: data.profile.middleName,
            phone: data.profile.phone
          };
          this.profile['id'] = data.profile.id;
          this.user.profile = data.profile;

          this.errorService.addSuccessMessage(
            `По почте ${email} обнаружены данные профиля. ФИО и номер телефона были заменены`
          );
        }

        this.loadingProfile = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.loadingProfile = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public isExternalUserActive(user: ExternalUserFormInterface): boolean {
    return user.profile?.status === ProfileStatusEnum.ACTIVE && !user['deleted'] && !user['banned'];
  }

  public onAdminAccessChange() {
    const accessControl = this.user.accessControl;
    if (!accessControl.admin) {
      Object.keys(accessControl).forEach((key) => {
        if (key !== 'admin') {
          accessControl[key] = true;
        }
      });
    }
  }

  public updatePermissions(user: ExternalUserFormInterface) {
    this.user.accessControl = user.accessControl;
  }
}
